import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Tabs } from "@shared/ui/tabs";

interface LogDetailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  logData: any;
}

export const LogDetailDialog: React.FC<LogDetailDialogProps> = ({
  isOpen,
  onClose,
  logData,
}) => {
  if (!logData) return null;

  let requestContent = "No request data available";
  if (logData.request) {
    const { method, url, headers, body } = logData.request;
    const headersStr = Object.entries(headers || {})
      .map(([k, v]) => `${k}: ${v}`)
      .join("\n");
    const bodyStr = body ? `\n\n${JSON.stringify(body, null, 2)}` : "";
    requestContent = `${method || "GET"} ${url || ""}\n${headersStr}${bodyStr}`;
  }

  let responseContent = "No response data available";
  if (logData.response) {
    responseContent = JSON.stringify(logData.response, null, 2);
  }

  const titleText =
    logData.operation === logData.flow
      ? logData.operation
      : `${logData.operation} ${logData.flow}`;

  const CodeBlock = ({ content }: { content: string }) => (
    <div className="bg-[#0f172a] text-gray-300 p-4 rounded-xl text-sm font-mono overflow-x-auto mt-4 whitespace-pre">
      {/* Simple regex to colorize JSON/Headers for visual parity with the mockup */}
      {content.split("\n").map((line, i) => {
        if (line.includes(": ")) {
          const [key, ...rest] = line.split(": ");
          const value = rest.join(": ");
          return (
            <div key={i}>
              <span className="text-blue-400">{key}</span>:{" "}
              <span
                className={
                  value.startsWith('"') ? "text-emerald-400" : "text-gray-300"
                }
              >
                {value}
              </span>
            </div>
          );
        }
        return <div key={i}>{line}</div>;
      })}
    </div>
  );

  const tabComponents = {
    request: {
      label: "Request",
      component: <CodeBlock content={requestContent} />,
    },
    response: {
      label: "Response",
      component: <CodeBlock content={responseContent} />,
    },
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col gap-0 p-0 overflow-hidden">
        <div className="p-6 pb-4 shrink-0">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              {titleText} <span className="text-gray-400">·</span>{" "}
              {logData.recordId}
            </DialogTitle>
          </DialogHeader>
        </div>

        <div className="px-6 py-2 border-t border-b bg-gray-50/50 flex-1 overflow-y-auto">
          <Tabs
            defaultValue="request"
            className="w-full"
            tabsListClassName="bg-white border p-1"
            tabComponents={tabComponents}
            disableUrlSync={true}
          />
        </div>

        <DialogFooter className="p-4 bg-gray-50/50 rounded-b-lg shrink-0">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
